import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      if (tries >= 20) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-2xl border border-[#1e5fe0]/25 bg-white/85 p-8 text-center"
        data-testid="payment-success-card"
      >
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-[#2f74f0] animate-spin mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-[#2e2015]">Confermiamo il pagamento…</h1>
            <p className="text-[#6f5c47] mt-2 text-sm">Attendi un istante, stiamo accreditando i tuoi crediti.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="grid place-items-center h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 mx-auto mb-4">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </span>
            <h1 className="font-serif text-2xl text-[#2e2015]">Pagamento riuscito!</h1>
            <p className="text-[#6f5c47] mt-2 text-sm">I crediti sono stati aggiunti al tuo account.</p>
            <div className="mt-6 flex flex-col gap-3">
              <Button
                data-testid="payment-goto-studio-btn"
                onClick={() => navigate("/studio")}
                className="bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full h-11"
              >
                Crea un outfit
              </Button>
              <Button
                data-testid="payment-goto-credits-btn"
                variant="outline"
                onClick={() => navigate("/credits")}
                className="border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 bg-transparent rounded-full h-11"
              >
                <Coins className="mr-2 h-4 w-4" /> Vedi il saldo
              </Button>
            </div>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <span className="grid place-items-center h-16 w-16 rounded-full bg-red-500/10 border border-red-500/30 mx-auto mb-4">
              <XCircle className="h-9 w-9 text-red-500" />
            </span>
            <h1 className="font-serif text-2xl text-[#2e2015]">
              {state === "timeout" ? "Conferma in corso" : "Pagamento non completato"}
            </h1>
            <p className="text-[#6f5c47] mt-2 text-sm">
              {state === "timeout"
                ? "Il pagamento potrebbe richiedere qualche minuto. Controlla il saldo tra poco."
                : "Non è stato possibile completare il pagamento. Riprova dalla pagina Crediti."}
            </p>
            <Button
              data-testid="payment-back-credits-btn"
              onClick={() => navigate("/credits")}
              className="mt-6 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full h-11 w-full"
            >
              Torna ai Crediti
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
